import { cn } from '@infinex/ui-styles';
import type { EmblemProps } from '@infinex/ui/components';

import { NftImage } from '../nfts';

type EmblemSize = NonNullable<EmblemProps['size']>;

/**
 * The slice of a collectible the emblem actually renders. Kept structural (not
 * the full `@infinex/types` Collectible) so dataset-derived view models — e.g.
 * the portfolio's `CollectibleCardView`, whose `lastUpdatedAt` widens to a
 * string — can pass their card without a cast.
 */
type CollectibleEmblemCard = {
  assetAddress: string;
  name: string;
  imageUrl?: string | null;
};

// Width is pinned to roughly the square Emblem footprint for each size; height
// is left to follow the card's natural aspect ratio. The matching `min-h` is a
// floor so the box doesn't collapse to zero while the image loads.
const SIZE_CLASS: Record<EmblemSize, string> = {
  xsmall: 'w-5 min-h-5',
  small: 'w-6 min-h-6',
  medium: 'w-8 min-h-8',
  large: 'w-10 min-h-10',
  xlarge: 'w-14 min-h-14',
};

/**
 * Emblem for a Collector Crypt card. Unlike Patron/NFT collection avatars, a
 * collectible always shows the actual card, so we preserve its real aspect
 * ratio (via `NftImage`) rather than cropping it into the square `Emblem`.
 *
 * Shared across the activity log and the send/move processing + review surfaces
 * so a card looks identical everywhere it appears.
 */
export function CollectibleEmblem({
  card,
  size = 'medium',
  className,
}: {
  card: CollectibleEmblemCard;
  size?: EmblemProps['size'];
  className?: string;
}) {
  const sizeClass = SIZE_CLASS[size ?? 'medium'];

  return (
    <NftImage
      identifier={card.assetAddress}
      imageUrl={card.imageUrl ?? undefined}
      alt={card.name}
      className={cn('shrink-0 overflow-hidden', sizeClass, className)}
    />
  );
}
